/**
 * Format a date as a relative time string (e.g., "2 hours ago")
 */
export function formatRelativeTime(date) {
    const now = new Date();
    const then = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);
    if (diffInSeconds < 60) {
        return 'just now';
    }
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    }
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
        return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
        return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
    }
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
        return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    }
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
}
/**
 * Format a date as a short date string in the user's local timezone and locale
 * (e.g., "Jan 1, 2024" for en-US, "1 Jan 2024" for en-GB, "1 Jan 2024" for id-ID)
 */
export function formatShortDate(date) {
    const d = new Date(date);
    return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}
/**
 * Format a date as a full date string in the user's local timezone and locale
 * (e.g., "January 1, 2024 at 3:45 PM" for en-US, "1 January 2024, 15:45" for id-ID)
 */
export function formatFullDate(date) {
    const d = new Date(date);
    return d.toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}
/**
 * Format a date as a local date string (YYYY-MM-DD) in the user's timezone
 * Note: Uses local timezone, not UTC
 */
export function formatISODate(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
/**
 * Format a time in the user's local timezone and locale
 * (e.g., "3:45 PM" for en-US, "15:45" for most other locales)
 */
export function formatTime(date) {
    const d = new Date(date);
    return d.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
    });
}
/**
 * Format a date and time in the user's local timezone and locale
 * (e.g., "Jan 1, 2024, 3:45 PM" for en-US)
 */
export function formatDateTime(date) {
    const d = new Date(date);
    return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}
/**
 * Check if a date is in the past
 */
export function isPast(date) {
    return new Date(date) < new Date();
}
/**
 * Check if a date is in the future
 */
export function isFuture(date) {
    return new Date(date) > new Date();
}
/**
 * Add days to a date
 */
export function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}
/**
 * Get the start of today
 */
export function startOfToday() {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
}
/**
 * Get the end of today
 */
export function endOfToday() {
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return now;
}
